import React, { FC, useEffect, useRef, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import LegalReacceptanceAgreementForm from 'containers/legal-reacceptance/LegalReacceptanceAgreementForm';
import LegalReacceptanceShell from 'containers/legal-reacceptance/LegalReacceptanceShell';
// eslint-disable-next-line max-len
import LegalReacceptanceIntroOverviewScreen from 'containers/legal-reacceptance/screens/LegalReacceptanceIntroOverviewScreen';
import WalletBankAccountScreen from 'containers/legal-reacceptance/screens/WalletBankAccountScreen';
import LegalReacceptanceSuccessScreen from 'containers/legal-reacceptance/screens/LegalReacceptanceSuccessScreen';
import {
  CleverTapEventsLegalReacceptance,
  LegalAgreementAcceptancePayload,
  LegalReacceptanceContent,
  WalletBankAccountPayload,
  WalletBankViewSource
} from 'containers/legal-reacceptance/legalReacceptance.model';
import {
  useConfirmLegalReacceptanceV2Mutation,
  useSubmitWalletBankAccountMutation
} from 'containers/legal-reacceptance/api';
import { mergeWalletWizardTermsContent } from 'containers/legal-reacceptance/mergeWalletWizardTermsContent';
import { logAnalyticEvent } from 'utils/analytics';
import { FireBaseCollections } from 'utils/firebase';
import { FirebaseStorage } from 'services/firebase';
import { useAppDispatch } from 'hooks';
import { showNotifier } from 'containers/app/appSlice';
import { NotifierTypes } from 'constants/notifierConstants';

export type LegalReacceptanceWizardStep = 'intro' | 'terms' | 'bank' | 'success';

type LegalReacceptanceFlowSharedProps = {
  open: boolean;
  teamId: number;
  /** From Firestore `LegalReacceptanceWalletBankScreenShownCount` (0 if missing). */
  walletBankScreenShownCount: number;
  /** Max impressions allowed for wallet bank screen from Remote Config. */
  walletBankScreenMaxShowCount: number;
  /** Force bank step visibility regardless of counter cap. */
  forceShowWalletBankStep?: boolean;
  /** When true, only the terms modal. When false, the full 4-step wizard (intro → terms → bank → success). */
  hasWalletEligibleBankAccount: boolean;
  onRefreshTeamData: () => void;
  onClose: () => void;
  walletBankViewSource?: WalletBankViewSource;
};

export type LegalReacceptanceFlowProps =
  | (LegalReacceptanceFlowSharedProps & {
      variant?: 'full';
      content: LegalReacceptanceContent;
    })
  | (LegalReacceptanceFlowSharedProps & {
      variant: 'walletReminder';
      content?: undefined;
    });

const FALLBACK_WALLET_BANK_SUBMIT_ERROR_MESSAGE =
  'Unable to submit bank account details right now. Please verify the details and try again.';

const getWalletBankSubmitErrorDetails = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const errorData = (error as { data?: { details?: unknown } })?.data;
  const details = errorData?.details;
  if (typeof details !== 'string') {
    return null;
  }
  const trimmedDetails = details.trim();
  return trimmedDetails.length > 0 ? trimmedDetails : null;
};

const LegalReacceptanceFlow: FC<LegalReacceptanceFlowProps> = props => {
  const {
    open,
    teamId,
    walletBankScreenShownCount,
    walletBankScreenMaxShowCount,
    forceShowWalletBankStep = false,
    hasWalletEligibleBankAccount,
    onRefreshTeamData,
    onClose,
    walletBankViewSource
  } = props;
  const variant = props.variant ?? 'full';
  const content = variant === 'full' ? props.content : undefined;
  const dispatch = useAppDispatch();

  const [wizardStep, setWizardStep] = useState<LegalReacceptanceWizardStep>('intro');
  const [reminderStep, setReminderStep] = useState<'bank' | 'success'>('bank');
  const bankStepIncrementedRef = useRef(false);
  const [confirmLegalReacceptanceV2, { isLoading: isConfirmingV2 }] = useConfirmLegalReacceptanceV2Mutation();
  const [submitWalletBankAccount, { isLoading: isSubmittingWallet }] = useSubmitWalletBankAccountMutation();

  useEffect(() => {
    if (!open) {
      setWizardStep('intro');
      setReminderStep('bank');
      bankStepIncrementedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || variant !== 'full') return;
    logAnalyticEvent(CleverTapEventsLegalReacceptance.webLegalReacceptanceModalView);
  }, [open, variant]);

  useEffect(() => {
    if (variant === 'walletReminder' || hasWalletEligibleBankAccount || !open) {
      return;
    }
    if (wizardStep !== 'bank') {
      bankStepIncrementedRef.current = false;
      return;
    }
    if (!forceShowWalletBankStep && walletBankScreenShownCount >= walletBankScreenMaxShowCount) {
      return;
    }
    if (bankStepIncrementedRef.current) {
      return;
    }
    bankStepIncrementedRef.current = true;
    void FirebaseStorage.incrementTeamsCollectionField({
      teamId: teamId.toString(),
      collectionFieldName: FireBaseCollections.Teams.fields.legalReacceptanceWalletBankScreenShownCount
    });
  }, [
    variant,
    open,
    wizardStep,
    hasWalletEligibleBankAccount,
    walletBankScreenShownCount,
    walletBankScreenMaxShowCount,
    forceShowWalletBankStep,
    teamId
  ]);

  const handleTermsV2 = async (payload: LegalAgreementAcceptancePayload) => {
    await confirmLegalReacceptanceV2({ teamId, payload }).unwrap();
    onRefreshTeamData();
  };

  const handleWalletSubmit = async (payload: WalletBankAccountPayload) => {
    try {
      await submitWalletBankAccount({ teamId, payload }).unwrap();
      onRefreshTeamData();
      if (variant === 'walletReminder') {
        setReminderStep('success');
        return;
      }
      setWizardStep('success');
    } catch (error) {
      const submitErrorDetails = getWalletBankSubmitErrorDetails(error);
      dispatch(
        showNotifier({
          message: {
            primaryMessage: submitErrorDetails ?? FALLBACK_WALLET_BANK_SUBMIT_ERROR_MESSAGE
          },
          type: NotifierTypes.ERROR,
          showClose: true
        })
      );
    }
  };

  const handleSkipBank = () => {
    onRefreshTeamData();
    onClose();
  };

  const handleSuccessDone = () => {
    onRefreshTeamData();
    onClose();
  };

  if (hasWalletEligibleBankAccount && content) {
    return (
      <CustomModal open={open} enableOutsideClick={false} width="w-full md:w-[450px]" height="h-auto md:h-[660px]">
        <LegalReacceptanceAgreementForm
          content={content}
          isConfirming={isConfirmingV2}
          onConfirm={async payload => {
            await handleTermsV2(payload);
            onClose();
          }}
        />
      </CustomModal>
    );
  }

  if (variant === 'walletReminder') {
    return (
      <CustomModal open={open} enableOutsideClick={false} width="w-full md:w-[450px]" height="h-auto md:h-[660px]">
        <div className="flex h-full min-h-0 flex-col">
          {reminderStep === 'bank' && (
            <LegalReacceptanceShell currentStep={3} showProgressBar={false}>
              <WalletBankAccountScreen
                onSubmit={handleWalletSubmit}
                onSkip={handleSkipBank}
                isSubmitting={isSubmittingWallet}
                viewSource={walletBankViewSource}
                walletBankScreenSeenCount={walletBankScreenShownCount + 1}
              />
            </LegalReacceptanceShell>
          )}
          {reminderStep === 'success' && (
            <LegalReacceptanceShell currentStep={4} showProgressBar={false}>
              <LegalReacceptanceSuccessScreen onBackToApp={handleSuccessDone} />
            </LegalReacceptanceShell>
          )}
        </div>
      </CustomModal>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <CustomModal open={open} enableOutsideClick={false} width="w-full md:w-[450px]" height="h-auto md:h-[660px]">
      <div className="flex h-full min-h-0 flex-col">
        {wizardStep === 'intro' && (
          <LegalReacceptanceShell currentStep={1}>
            <LegalReacceptanceIntroOverviewScreen onGetStarted={() => setWizardStep('terms')} />
          </LegalReacceptanceShell>
        )}
        {wizardStep === 'terms' && (
          <LegalReacceptanceShell currentStep={2}>
            <LegalReacceptanceAgreementForm
              content={mergeWalletWizardTermsContent(content)}
              isConfirming={isConfirmingV2}
              onBack={() => setWizardStep('intro')}
              onConfirm={async payload => {
                await handleTermsV2(payload);
                setWizardStep(
                  !forceShowWalletBankStep && walletBankScreenShownCount >= walletBankScreenMaxShowCount
                    ? 'success'
                    : 'bank'
                );
              }}
            />
          </LegalReacceptanceShell>
        )}
        {wizardStep === 'bank' && (
          <LegalReacceptanceShell currentStep={3}>
            <WalletBankAccountScreen
              onSubmit={handleWalletSubmit}
              onSkip={handleSkipBank}
              isSubmitting={isSubmittingWallet}
              viewSource={walletBankViewSource}
              walletBankScreenSeenCount={walletBankScreenShownCount + 1}
            />
          </LegalReacceptanceShell>
        )}
        {wizardStep === 'success' && (
          <LegalReacceptanceShell currentStep={4}>
            <LegalReacceptanceSuccessScreen onBackToApp={handleSuccessDone} />
          </LegalReacceptanceShell>
        )}
      </div>
    </CustomModal>
  );
};

export default LegalReacceptanceFlow;
